import type { Ref } from 'vue';
import { useRoute } from 'vue-router';

import { decodeFromTiptap } from '@/app/scheduler/utils/tiptap';
import { ACTION_TYPES, INTENT_TYPES } from '@/components/VisionAi/constants';
import { buildRequestContext, usePageFilters } from '@/components/VisionAi/utils/request-context';
import { VISION_AI_SENDER } from '@/config';
import { useIdentityStore } from '@/stores/identity';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

/**
 * Handles sending freeform text messages from the side panel prompt box.
 *
 * This mirrors the old VisionAiTextbox submit logic — NOT visionAiStore.startConversation(),
 * which is designed for prompt buttons with route-specific intent types. Freeform text always
 * uses UNIFIED with a simple { currentBrandId } intent query.
 *
 * The PromptBox uses TipTap, so promptValue contains HTML (e.g. "<p>hello</p>").
 * decodeFromTiptap strips tags and unescapes before sending plain text to the API.
 *
 * The user message doesn't appear immediately from this call — it arrives via WebSocket
 * (VISION_AI_MESSAGE_CREATED) after the backend echoes it back. The loading state is set
 * synchronously by createMessage -> addLoadingStates before the API call.
 */
export function useSubmitUserPrompt(promptValue: Ref<string>) {
  const route = useRoute();
  const identityStore = useIdentityStore();
  const visionAiStore = useVisionAiStore();
  const visionAiConversationsStore = useVisionAiConversationsStore();
  const visionAiMessagesStore = useVisionAiMessagesStore();
  const pageFilters = usePageFilters();

  async function handleSubmit() {
    const message = decodeFromTiptap(promptValue.value).trim();
    if (!message) return;
    promptValue.value = '';

    if (visionAiConversationsStore.summariesSelected) {
      visionAiStore.startNewChat();
    }

    let conversationId = visionAiConversationsStore.currentConversationId;

    if (!conversationId) {
      const conversation = await visionAiConversationsStore.createConversation();
      conversationId = conversation?.id;
    }

    const currentBrandId = identityStore.currentBrand?.id;
    if (!currentBrandId) return;

    await visionAiMessagesStore.createMessage({
      conversationId,
      sender: VISION_AI_SENDER.USER,
      message,
      intentType: INTENT_TYPES.UNIFIED,
      intentQuery: { currentBrandId },
      actionType: ACTION_TYPES.QUESTION,
      usedPrompt: false,
      requestContext: buildRequestContext(route, currentBrandId, pageFilters.value),
    });
  }

  return { handleSubmit };
}
